import './App.css';
import './tailwind.css';
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { client } from './axiosClient';
import { Toaster } from './components/ui/toaster';

import Layout from './components/Layout';
import OutfitUpload from './components/OutfitUpload';
import OutfitGallery from './components/OutfitGallery copy';
import OutfitSuggestion from './components/OutfitSuggestion';
import LoginRegister from './components/LoginRegister';
import Profile from './components/Profile';
import Dashboard from './components/Dashboard';
import Favorites from './components/Favorites';
import OutfitDetail from './components/OutfitDetail';
import Feedback from './components/Feedback';
import Pricing from './components/Pricing';
import WelcomeOnboarding from './components/WelcomeOnboarding';
import Help from './components/Help';

// Custom hook to handle page titles
const usePageTitle = () => {
  const location = useLocation();

  useEffect(() => {
    const getPageTitle = () => {
      switch (location.pathname) {
        case '/dashboard':
          return 'Dashboard | StyleGen AI';
        case '/my-closet':
          return 'My Closet | StyleGen AI';
        case '/generate':
          return 'Generate Outfit | StyleGen AI';
        case '/upload':
          return 'Upload Outfit | StyleGen AI';
        case '/profile':
          return 'Profile | StyleGen AI';
        case '/favorites':
          return 'Favorites | StyleGen AI';
        case '/feedback':
          return 'Feedback | StyleGen AI';
        case '/pricing':
          return 'Pricing | StyleGen AI';
        case '/login':
          return 'Sign In | StyleGen AI';
        case '/welcome':
          return 'Welcome | StyleGen AI';
        case '/help':
          return 'Help | StyleGen AI';
        default:
          if (location.pathname.startsWith('/my-closet/')) {
            return 'Outfit Details | StyleGen AI';
          }
          return 'StyleGen AI';
      }
    };

    document.title = getPageTitle();
  }, [location]);
};

function AppContent() {
  const [currentUser, setCurrentUser] = useState(null);
  const [refreshGallery, setRefreshGallery] = useState(0);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const posthog = usePostHog();

  // Use the page title hook
  usePageTitle();

  const triggerGalleryRefresh = useCallback(() => {
    setRefreshGallery(prev => prev + 1);
  }, []);

  const handleUserUpdate = useCallback((updatedUser) => {
    setCurrentUser(updatedUser);
    
    // Update PostHog user properties when user data changes
    if (updatedUser && updatedUser.id) {
      posthog.identify(updatedUser.id.toString(), {
        email: updatedUser.email,
        first_name: updatedUser.first_name,
        last_name: updatedUser.last_name,
        has_completed_onboarding: updatedUser.has_completed_onboarding,
        gender: updatedUser.gender,
        subscription_plan: updatedUser.subscriptionPlan,
        has_subscription: updatedUser.hasSubscription
      });
    }
  }, [posthog]);

  const checkAuthStatus = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token') || localStorage.getItem('access_token');
    const refreshToken = urlParams.get('refresh_token') || localStorage.getItem('refresh_token');

    if (accessToken && refreshToken) {
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      client.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      // Remove the tokens from the URL
      window.history.replaceState({}, document.title, window.location.pathname);

      try {
        const [userResponse, statsResponse] = await Promise.all([
          client.get("/api/user/"),
          client.get("/api/user/statistics/")
        ]);

        const userData = {
          ...userResponse.data.user,
          hasSubscription: statsResponse.data.has_subscription,
          subscriptionPlan: statsResponse.data.subscription_plan
        };

        setCurrentUser(userData);

        // Identify user with PostHog as soon as we have user data
        if (userData && userData.id) {
          posthog.identify(userData.id.toString(), {
            email: userData.email,
            first_name: userData.first_name,
            last_name: userData.last_name,
            has_completed_onboarding: userData.has_completed_onboarding,
            gender: userData.gender,
            subscription_plan: userData.subscriptionPlan,
            has_subscription: userData.hasSubscription,
            created_at: userData.date_joined
          });

          // Set user ID as alias
          posthog.alias(userData.id.toString());

          // Track app load for authenticated user
          posthog.capture('app_loaded', {
            user_id: userData.id,
            has_completed_onboarding: userData.has_completed_onboarding,
            subscription_plan: userData.subscriptionPlan,
            page: location.pathname
          });
        }

        // Set user email in data layer for GTM
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'user_email': userData.email
        });

      } catch (error) {
        console.error("Error fetching user data:", error);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        
        // Reset PostHog on auth error
        posthog.reset();
      }
    } else {
      // Track app load for anonymous user
      posthog.capture('app_loaded', {
        user_authenticated: false,
        page: location.pathname
      });
    }
    setLoading(false);
  }, [posthog, location.pathname]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  if (loading) {
    return <div>Loading...</div>;
  }

  const handleLogout = () => {
    // Track logout event before resetting
    posthog.capture('user_logged_out', {
      page: location.pathname
    });
    
    // Reset PostHog user identification on logout
    posthog.reset();
    
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete client.defaults.headers.common['Authorization'];
    setCurrentUser(null);
  };

  const isWelcomePage = location.pathname === '/welcome';
  
  // If user is not logged in and trying to access welcome page, redirect to login
  if (!currentUser && isWelcomePage) {
    return <Navigate to="/login" />;
  }
  
  // If user is logged in but hasn't completed onboarding, redirect to welcome page
  if (currentUser && !currentUser.has_completed_onboarding && !isWelcomePage) {
    return <Navigate to="/welcome" />;
  }

  // For welcome page, don't wrap in Layout (only for authenticated users)
  if (isWelcomePage && currentUser) {
    return (
      <>
        <Routes>
          <Route path="/welcome" element={
            <WelcomeOnboarding 
              onComplete={() => {
                console.log('Onboarding completed');
                setCurrentUser({
                  ...currentUser,
                  has_completed_onboarding: true
                });
                navigate('/dashboard');
              }} 
            />
          } />
        </Routes>
        <Toaster />
      </>
    );
  }

  return (
    <>
      <Layout 
        isLoggedIn={!!currentUser} 
        onLogout={handleLogout}
        username={currentUser?.first_name || currentUser?.email}
        avatar={currentUser?.avatar}
        user={currentUser}
      >
        <Routes>
          {currentUser ? (
            // Authenticated routes
            <>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/welcome" element={<Navigate to="/dashboard" />} />
              <Route path="/my-closet" element={<OutfitGallery refreshTrigger={refreshGallery} />} />
              <Route path="/generate" element={<OutfitSuggestion />} />
              <Route path="/upload" element={<OutfitUpload onUploadSuccess={triggerGalleryRefresh} />} />
              <Route path="/profile" element={
                <Profile 
                  user={currentUser}
                  onUserUpdate={handleUserUpdate}
                />
                } />
              <Route path="/favorites" element={<Favorites />} />
              <Route path="/my-closet/:id" element={<OutfitDetail />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/help" element={<Help />} />
              <Route path="/login" element={<Navigate to="/dashboard" />} />
            </>
          ) : (
            // Unauthenticated routes
            <>
              <Route path="/login" element={<LoginRegister setCurrentUser={setCurrentUser} />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/help" element={<Help />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </>
          )}
        </Routes>
      </Layout>
      <Toaster />
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
